import { Box, Stack, Typography } from '@mui/material';
import { sam } from '../../DataAccessLayer/services';
import { Time } from '../common/MomentTime';
import { getColumns } from './JobReportColumns';
import { toTitleCase } from '../common/utils';
import { getCampusFromSiteCode } from '../SAM/utils';
import { PortalLinearProgress } from '../common/PortalLinearProgress';

const smallScreenDateFormat = 'MMM D, YYYY h:mm A';
const dateFormat = 'LLL';

export const getJobReportConfig = ({ record, isSM, isAdmin }) => {
  return {
    autoAssignmentReport: {
      jobId: record?.DATE + '_' + record?.CREATED_AT,
      task: 'Assigned',
      getJobs: sam.batch().noAssignments.jobs,
      getDetails: sam.batch().noAssignments.report,
      getRowId: (row) => row.ID + '_' + row.CREATED_AT,
      processedCount: record?.PROCESSED_COUNT,
      successCount: record?.ASSIGNMENTS_COUNT,
      skipCount: record?.NOMATCHES_COUNT,
      errorCount: record?.ERRORS_COUNT,
      totalCount: record?.TOTAL_COUNT,
      columns: getColumns(
        [
          {
            field: 'STUDENT_ID',
            headerName: 'Student ID',
          },
          {
            field: 'STUDENT_PIDM',
            headerName: 'Student PIDM',
          },
          {
            field: 'STUDENT_FIRST_NAME',
            headerName: 'Student First Name',
          },
          {
            field: 'STUDENT_LAST_NAME',
            headerName: 'Student Last Name',
          },
          {
            field: 'STUDENT_LEVEL',
            headerName: 'Student Level',
          },
          {
            field: 'DEPARTMENT',
            headerName: 'DEPARTMENT',
          },
          {
            field: 'COLLEGE_CODE',
            headerName: 'College Code',
          },
          {
            field: 'DEGREE_CODE',
            headerName: 'Degree Code',
          },
          {
            field: 'MAJOR_CODE',
            headerName: 'Major Code',
          },
          {
            field: 'CONCENTRATION_CODE',
            headerName: 'Concentration Code',
          },
          {
            field: 'CAMPUS_CODE',
            headerName: 'Campus Code',
          },
          {
            field: 'CLASS_CODE',
            headerName: 'Class Code',
          },
          {
            field: 'STYP_CODE',
            headerName: 'Student Type Code',
          },
          {
            field: 'EFFECTIVE_TERM_CODE',
            headerName: 'Effective Term Code',
          },
          {
            field: 'ADVISOR_PIDM',
            headerName: 'Advisor PIDM',
          },
          {
            field: 'ADVISOR_NAME',
            headerName: 'Advisor Name',
          },
          {
            field: 'ADVISOR_TYPE_CODE',
            headerName: 'Advisor Type Code',
          },
          {
            field: 'PRIMARY_INDICATOR',
            headerName: 'Primary Indicator',
          },
          {
            field: 'CREATED_AT',
            headerName: 'Created At',
            valueGetter: (params, row) =>
              Time.University(row.CREATED_AT).format('MM/DD/YYYY h:mm A'),
          },
        ],
        isAdmin,
      ),

      summary: [
        {
          name: 'JOB INFO',
          value: [
            {
              label: 'Started',
              data: Time.University(record?.CREATED_AT).format(
                isSM ? smallScreenDateFormat : dateFormat,
              ),
              dataClassName: 'pb-sm-3 pb-md-0', // only this one needs the extra padding
            },
            {
              label: 'Completed',
              data: record?.COMPLETED_AT
                ? Time.University(record?.COMPLETED_AT).format(
                    isSM ? smallScreenDateFormat : dateFormat,
                  )
                : 'N/A',
            },
            {
              label: 'Performed By',
              data: record?.ORIGIN || 'N/A',
            },
          ],
        },
        {
          name: 'ITEM PROCESSED',
          value: [
            {
              label: 'Items Processed',
              data: record?.PROCESSED_COUNT,
            },
            {
              label: 'Successfully Processed',
              data: record?.ASSIGNMENTS_COUNT,
              className: 'subData',
            },
            {
              label: 'Processing Errors',
              data: record?.ERRORS_COUNT,

              className: 'myOdu__error subData',
            },
          ],
        },
        {
          name: 'ASSIGNMENTS',
          value: [
            {
              label: 'Successfully Processed',
              data: record?.PROCESSED_COUNT,
            },
            {
              label: 'Assigned',
              data: record?.ASSIGNMENTS_COUNT,
              className: 'subData',
            },
            {
              label: 'No Advisor Matched',
              data: record?.NOMATCHES_COUNT,
              className: 'myOdu__error subData',
            },
          ],
        },
      ],
    },
    inactiveAutoUnassignmentReport: {
      jobId: record?.DATE + '_' + record?.CREATED_AT,
      task: 'Unassigned',
      getJobs: sam.batch().inactive.jobs,
      getDetails: sam.batch().inactive.report,
      getRowId: (row) => row.JOB_ID + '_' + row.CREATED_AT,
      processedCount: record?.PROCESSED_COUNT,
      successCount: record?.SUCCESS_COUNT,
      skipCount: record?.SKIP_COUNT,
      errorCount: record?.ERRORS_COUNT,
      totalCount: record?.TOTAL_COUNT,
      columns: getColumns(
        [
          {
            field: 'MIDAS',
            headerName: 'MIDAS',
          },
          {
            field: 'SGRADVR_PIDM',
            headerName: 'Student PIDM',
          },
          {
            field: 'SPRIDEN_FIRST_NAME',
            headerName: 'First Name',
          },
          {
            field: 'SPRIDEN_LAST_NAME',
            headerName: 'Last Name',
          },
          {
            field: 'SGRADVR_TERM_CODE_EFF',
            headerName: 'Effective Term Code',
          },
          { field: 'SGRADVR_ADVR_PIDM', headerName: 'Advisor PIDM' },
          {
            field: 'SGRADVR_PRIM_IND',
            headerName: 'Primary Indicator',
          },
          {
            field: 'CREATED_AT',
            headerName: 'Created At',
            valueGetter: (params, row) =>
              Time.University(row.CREATED_AT).format('MM/DD/YYYY h:mm A'),
          },
        ],
        isAdmin,
      ),

      summary: [
        {
          name: 'JOB INFO',
          value: [
            {
              label: 'Started',
              data: Time.University(record?.CREATED_AT).format(
                isSM ? smallScreenDateFormat : dateFormat,
              ),
              dataClassName: 'pb-sm-3 pb-md-0', // only this one needs the extra padding
            },
            {
              label: 'Completed',
              data: record?.COMPLETED_AT
                ? Time.University(record?.COMPLETED_AT).format(
                    isSM ? smallScreenDateFormat : dateFormat,
                  )
                : 'N/A',
            },
            {
              label: 'Performed By',
              data: record?.ORIGIN || 'N/A',
            },
          ],
        },
        {
          name: 'ITEM PROCESSED',
          value: [
            {
              label: 'Items Processed',
              data: record?.PROCESSED_COUNT,
            },
            {
              label: 'Successfully Processed',
              data: record?.SUCCESS_COUNT,
              className: 'subData',
            },
            {
              label: 'Processing Errors',
              data: record?.ERROR_COUNT,
              className: 'myOdu__error subData',
            },
          ],
        },
        {
          name: 'UNASSIGNMENTS',
          value: [
            {
              label: 'Successfully Processed',
              data: record?.PROCESSED_COUNT,
            },
            {
              label: 'Unassigned',
              data: record?.SUCCESS_COUNT,
              className: 'subData',
            },
          ],
        },
      ],
    },
    diffMajorAutoUnassignmentReport: {
      jobId: record?.DATE + '_' + record?.CREATED_AT,
      task: 'Unassigned',
      getJobs: sam.batch().diffMajor.jobs,
      getDetails: sam.batch().diffMajor.report,
      getRowId: (row) => row.JOB_ID + '_' + row.CREATED_AT,
      processedCount: record?.PROCESSED_COUNT,
      successCount: record?.SUCCESS_COUNT,
      skipCount: record?.SKIP_COUNT,
      errorCount: record?.ERRORS_COUNT,
      totalCount: record?.TOTAL_COUNT,
      columns: getColumns(
        [
          {
            field: 'SPRIDEN_ID',
            headerName: 'Student ID',
          },
          {
            field: 'SPRIDEN_PIDM',
            headerName: 'Student PIDM',
          },
          {
            field: 'LASTNAME',
            headerName: 'Last Name',
            width: 100,
            valueGetter: (value, row) => {
              const lastName = row.SNAME.split(',')[0];
              return toTitleCase(lastName);
            },
          },
          {
            field: 'FIRSTNAME',
            headerName: 'First and Middle Name',
            width: 200,
            valueGetter: (value, row) => {
              const firstName = row.SNAME.split(',')[1];
              return toTitleCase(firstName);
            },
          },
          {
            field: 'MIDAS',
            headerName: 'MIDAS',
          },
          {
            field: 'EMAIL',
            headerName: 'Email',
          },
          {
            field: 'SGBSTDN_LEVL_CODE',
            headerName: 'Level',
          },
          {
            field: 'SGBSTDN_STYP_CODE',
            headerName: 'Student Type',
          },
          {
            field: 'CLAS_CODE',
            headerName: 'Class',
          },
          {
            field: 'SGBSTDN_DEGC_CODE_1',
            headerName: 'Degree',
          },
          {
            field: 'SGBSTDN_SITE_CODE',
            headerName: 'Site',
          },
          {
            field: 'CAMPUS',
            headerName: 'Campus',
            valueGetter: (value, row) => getCampusFromSiteCode(row.SGBSTDN_SITE_CODE),
          },
          {
            field: 'STDN_DEPT_CODE',
            headerName: 'Department',
            renderCell: (params) => {
              const departments = params.row.DEPARTMENTS;
              return (
                <Stack direction={'column'} gap={1}>
                  {departments.map((department) => {
                    return (
                      <Typography variant="p" sx={{ whiteSpace: 'nowrap' }} noWrap>
                        {department}
                      </Typography>
                    );
                  })}
                </Stack>
              );
            },
            valueGetter: (value, row) => {
              const departments = row.DEPARTMENTS;
              return departments.join(' ');
            },
          },
          {
            field: 'ADVR_NAME',
            headerName: 'Previous Advisor',
          },
          {
            field: 'TYPE',
            headerName: 'Type',
            renderCell: (params) => {
              const types = params.row.TYPES;
              return (
                <Stack direction={'column'} gap={1}>
                  {types.map((type) => {
                    return (
                      <Typography variant="p" sx={{ whiteSpace: 'nowrap' }} noWrap>
                        {type}
                      </Typography>
                    );
                  })}
                </Stack>
              );
            },
            valueGetter: (value, row) => {
              const types = row.TYPES;
              return types.join(' ');
            },
          },
          {
            field: 'FIELDOFSTUDY',
            headerName: 'Field of Study',
            width: 200,
            renderCell: (params) => {
              const fields = params.row.FIELD_OF_STUDIES;
              return (
                <Stack direction={'column'} gap={1}>
                  {fields.map((field) => {
                    return (
                      <Typography variant="p" sx={{ whiteSpace: 'nowrap' }} noWrap>
                        {field}
                      </Typography>
                    );
                  })}
                </Stack>
              );
            },
            valueGetter: (value, row) => {
              const fields = row.FIELD_OF_STUDIES;
              return fields.join(' ');
            },
          },
          {
            field: 'SGRADVR_PRIM_IND',
            headerName: 'Previous Primary Advisor',
          },
          {
            field: 'ENROLLED',
            headerName: 'Enrolled',
          },
          {
            field: 'CREATED_AT',
            headerName: 'Created At',
            valueGetter: (params, row) =>
              Time.University(row.CREATED_AT).format('MM/DD/YYYY h:mm A'),
          },
        ],
        isAdmin,
      ),

      summary: [
        {
          name: 'JOB INFO',
          value: [
            {
              label: 'Started',
              data: Time.University(record?.CREATED_AT).format(
                isSM ? smallScreenDateFormat : dateFormat,
              ),
              dataClassName: 'pb-sm-3 pb-md-0', // only this one needs the extra padding
            },
            {
              label: 'Completed',
              data: record?.COMPLETED_AT
                ? Time.University(record?.COMPLETED_AT).format(
                    isSM ? smallScreenDateFormat : dateFormat,
                  )
                : 'N/A',
            },
            {
              label: 'Performed By',
              data: record?.ORIGIN || 'N/A',
            },
          ],
        },
        {
          name: 'ITEM PROCESSED',
          value: [
            {
              label: 'Items Processed',
              data: record?.PROCESSED_COUNT,
            },
            {
              label: 'Successfully Processed',
              data: record?.SUCCESS_COUNT,
              className: 'subData',
            },
            {
              label: 'Processing Errors',
              data: record?.ERROR_COUNT,
              className: 'myOdu__error subData',
            },
          ],
        },
        {
          name: 'UNASSIGNMENTS',
          value: [
            {
              label: 'Successfully Processed',
              data: record?.PROCESSED_COUNT,
            },
            {
              label: 'Unassigned',
              data: record?.SUCCESS_COUNT,
              className: 'subData',
            },
          ],
        },
      ],
    },
  };
};
